import React, { useState, useEffect } from "react";
import { MdClose, MdBusiness, MdUpload, MdDelete, MdCheck, MdShield, MdFingerprint, MdRefresh } from "react-icons/md";
import { Company, SubscriptionType, getCompanyById } from "../../utils/api/endpoints/companies";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

import SubscriptionManager from "./SubscriptionManager";

interface CompanyEditModalProps {
  company: Company;
  onClose: () => void;
  onSave: (
    companyId: number,
    companyName: string,
    logoFile: File | null,
    subscriptionType: SubscriptionType | null,
    quickRunsQuota: number | null,
    comprehensiveRunsQuota: number | null,
    quickRunDuration: number | null,
    comprehensiveRunDuration: number | null,
    isArchived: boolean | null,
    subscriptionStartDate: string | null,
    subscriptionEndDate: string | null
  ) => void;
}

const CompanyEditModal: React.FC<CompanyEditModalProps> = ({
  company,
  onClose,
  onSave
}) => {
  const [companyName, setCompanyName] = useState(company.company_name);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(
    company.company_logo || null
  );
  
  // These are now legacy in terms of UI (handled by SubscriptionManager), 
  // but we keep them for the onSave call which still expects them for "Basic" overrides if any.
  const [subscriptionType, setSubscriptionType] = useState<SubscriptionType | null>(company.subscription_type || null);
  const [quickRunsQuota, setQuickRunsQuota] = useState<string>(company.quick_runs_quota?.toString() || "0");
  const [comprehensiveRunsQuota, setComprehensiveRunsQuota] = useState<string>(company.comprehensive_runs_quota?.toString() || "0");
  const [quickRunDuration, setQuickRunDuration] = useState<string>(company.quick_run_duration?.toString() || "15");
  const [comprehensiveRunDuration, setComprehensiveRunDuration] = useState<string>(company.comprehensive_run_duration?.toString() || "30");
  const [subscriptionStartDate, setSubscriptionStartDate] = useState<string>(company.subscription_start_date ? company.subscription_start_date.substring(0, 10) : "");
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string>(company.subscription_end_date ? company.subscription_end_date.substring(0, 10) : "");

  const [saving, setSaving] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo file size must be less than 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
    setLogoFile(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(company.company_logo || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    await onSave(
      company.company_id,
      companyName.trim(),
      logoFile,
      subscriptionType,
      parseInt(quickRunsQuota) || 0,
      parseInt(comprehensiveRunsQuota) || 0,
      parseInt(quickRunDuration) || 0,
      parseInt(comprehensiveRunDuration) || 0,
      company.is_archived || false,
      subscriptionStartDate || null,
      subscriptionEndDate || null
    );
    setSaving(false);
    toast.success("Organization Identity Updated");
  };

  const refreshCompany = async () => {
    try {
      const latest = await getCompanyById(company.company_id);
      if (latest) {
        setSubscriptionType(latest.subscription_type || null);
        setQuickRunsQuota(latest.quick_runs_quota?.toString() || "0");
        setComprehensiveRunsQuota(latest.comprehensive_runs_quota?.toString() || "0");
        setQuickRunDuration(latest.quick_run_duration?.toString() || "15");
        setComprehensiveRunDuration(latest.comprehensive_run_duration?.toString() || "30");
        setSubscriptionStartDate(latest.subscription_start_date ? latest.subscription_start_date.substring(0, 10) : "");
        setSubscriptionEndDate(latest.subscription_end_date ? latest.subscription_end_date.substring(0, 10) : "");
      }
    } catch (e) {
      console.error("Failed to refresh company data:", e);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4 overflow-hidden">
      <motion.div 
        initial={{ opacity: 0, scale: 0.98, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-5xl rounded-[2rem] bg-white shadow-2xl flex flex-col overflow-hidden max-h-[95vh] border border-gray-100"
      >
        {/* CLEAN MINIMAL HEADER */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-50 bg-white sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center shadow-lg">
              <MdShield className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">Organization Profile</h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Admin Management Hub</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-all group"
          >
            <MdClose className="text-xl group-hover:rotate-90 transition-transform" />
          </button>
        </div>

        {/* SCROLLABLE BODY */}
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {/* SECTION 1: IDENTITY BAR (Horizontal Layout) */}
          <div className="px-8 py-8 border-b border-gray-50 bg-gray-50/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] flex items-center gap-2">
                <MdFingerprint className="text-blue-600 text-sm" />
                Entity Specification
              </h3>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${company.is_archived ? "bg-rose-50 text-rose-500 border-rose-100" : "bg-emerald-50 text-emerald-600 border-emerald-100"}`}>
                  {company.is_archived ? "Restricted" : "Live Operation"}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[8px] font-black uppercase tracking-widest border border-gray-200 flex items-center gap-1">
                  ID: {company.company_id}
                  <MdRefresh className="cursor-pointer hover:text-blue-600 transition-colors" onClick={refreshCompany} />
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col lg:flex-row items-end gap-6 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex-1 w-full">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 block pl-1">Legal Organization Name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full h-11 bg-gray-50/50 rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-900 focus:border-blue-500/50 focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all"
                  placeholder="Organization Legal Title"
                  required
                />
              </div>

              <div className="w-full lg:w-72">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 block pl-1">Corporate Visual ID</label>
                <div className="flex items-center gap-3 bg-gray-50/50 p-2 rounded-xl border border-gray-200 border-dashed">
                  <div className="h-7 w-7 overflow-hidden rounded-lg bg-white flex items-center justify-center shadow-sm">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="h-full w-full object-contain" />
                    ) : (
                      <MdBusiness className="text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1 flex items-center gap-3">
                    <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="logo-upload" />
                    <label htmlFor="logo-upload" className="text-[9px] font-black text-blue-600 hover:text-blue-800 cursor-pointer uppercase tracking-widest transition-colors">
                      Change Asset
                    </label>
                    {logoPreview && (
                      <button type="button" onClick={handleRemoveLogo} className="text-[9px] font-black text-rose-500 hover:text-rose-700 uppercase tracking-widest transition-colors">
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="h-11 px-8 bg-gray-900 text-white rounded-xl font-black text-[9px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-black transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 group shrink-0"
                disabled={saving}
              >
                {saving ? "Syncing..." : (
                  <>
                    <MdCheck className="text-lg text-emerald-400" />
                    Update Settings
                  </>
                )}
              </button>
            </form>
          </div>

          {/* SECTION 2: SUBSCRIPTION REGISTRY */}
          <div className="bg-white">
            <SubscriptionManager companyId={company.company_id} onSync={refreshCompany} />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CompanyEditModal;
